import { alpha } from '@mui/material/styles';
import { getDropdownSurfaceActiveBackground, getDropdownSurfaceBackground, } from './dropdown-surface';
import { v2MenuItemDangerClass, v2MenuItemSizeClass } from './menu-item-modifiers';
/**
 * The v2 theme expressed as DELTAS from the v1 theme. `V2ThemeProvider`
 * deepmerges this over the surrounding v1 theme, so anything not listed here is
 * inherited from v1 unchanged.
 */
export const getV2ThemeOptions = () => {
    // Cast through `unknown`: this is a partial DELTA that `V2ThemeProvider`
    // deepmerges over the full v1 theme, so it need not satisfy the full augmented
    // `ThemeOptions` shape on its own.
    return {
        components: {
            MuiLink: {
                defaultProps: { underline: 'hover' },
                styleOverrides: {
                    root: ({ theme }) => ({
                        // Mockup link color = --primary teal (per mode), the same source the
                        // checked switch and focused field use.
                        color: theme.palette.custom.OV.sys.primary,
                        fontWeight: 600,
                    }),
                },
            },
            MuiSwitch: {
                styleOverrides: {
                    root: { width: 38, height: 22, padding: 0 },
                    switchBase: ({ theme }) => ({
                        padding: 0,
                        // 3px inset all round → 16px thumb travel to the far edge.
                        margin: 3,
                        transitionDuration: '180ms',
                        '&.Mui-checked': {
                            transform: 'translateX(16px)',
                            '& + .MuiSwitch-track': {
                                opacity: 1,
                                backgroundColor: theme.palette.custom.OV.sys.primary,
                            },
                            '& .MuiSwitch-thumb': {
                                backgroundColor: theme.palette.custom.OV.sys.onPrimary,
                            },
                        },
                        '&.Mui-disabled + .MuiSwitch-track': { opacity: 0.4 },
                        '&.Mui-disabled .MuiSwitch-thumb': { opacity: 0.6 },
                    }),
                    thumb: ({ theme }) => ({
                        width: 16,
                        height: 16,
                        boxShadow: 'none',
                        backgroundColor: theme.palette.custom.OV.sys.onSurfaceVariant,
                    }),
                    track: ({ theme }) => ({
                        borderRadius: 11,
                        opacity: 1,
                        backgroundColor: theme.palette.custom.OV.sys.surfaceContainerHighest,
                    }),
                },
            },
            MuiTabs: {
                styleOverrides: {
                    root: ({ theme }) => ({
                        minHeight: 'unset',
                        borderBottom: `1px solid ${theme.palette.custom.OV.sys.outlineVariant}`,
                    }),
                    flexContainer: { gap: 2 },
                    indicator: ({ theme }) => ({
                        height: 2,
                        borderRadius: 2,
                        backgroundColor: theme.palette.custom.OV.sys.secondary,
                    }),
                },
            },
            MuiTab: {
                styleOverrides: {
                    root: ({ theme }) => ({
                        textTransform: 'none',
                        minHeight: 'unset',
                        minWidth: 'auto',
                        padding: '8px 12px 12px',
                        fontSize: 13,
                        fontWeight: 600,
                        color: theme.palette.custom.OV.sys.onSurfaceVariant,
                        '&:hover': { color: theme.palette.custom.OV.sys.onBackground },
                        '&.Mui-selected': { color: theme.palette.custom.OV.sys.onBackground },
                    }),
                },
            },
            MuiMenu: {
                styleOverrides: {
                    paper: ({ theme }) => ({
                        borderRadius: '0.75rem',
                        border: `1px solid ${theme.palette.custom.OV.sys.outlineVariant}`,
                        // Same surface as `getDropdownPaperSx` (SelectMenu / AppMenu), so
                        // every dropdown in the app reads as one family.
                        backgroundColor: getDropdownSurfaceBackground(theme),
                        // Paper paints a white elevation overlay over the token in dark mode,
                        // which would lift this surface above the other dropdowns'.
                        backgroundImage: 'none',
                        // Same elevation MUI's Popover/Menu default to (elevation 8),
                        // reused rather than a mode-specific ad-hoc shadow.
                        boxShadow: theme.shadows[8],
                    }),
                    list: ({ theme }) => ({
                        padding: '0.3125rem',
                        // Doubled class outranks MUI's `.MuiMenuItem-root + .MuiDivider-root`
                        // spacing rule, which is injected later at equal specificity. The
                        // horizontal inset matches a row's own padding, so the rule lines up
                        // with the icon column instead of running the full card width.
                        '& .MuiDivider-root.MuiDivider-root': {
                            borderColor: theme.palette.custom.OV.sys.outlineVariant,
                            margin: '0.25rem 0.5625rem',
                        },
                    }),
                },
            },
            MuiMenuItem: {
                styleOverrides: {
                    root: ({ theme }) => ({
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5625rem',
                        padding: '0.5rem 0.5625rem',
                        borderRadius: '0.5rem',
                        fontSize: '0.78125rem',
                        fontWeight: 500,
                        minHeight: 'unset',
                        color: theme.palette.custom.OV.sys.onBackground,
                        // Same highlight as a SelectMenu row (`select-menu.elements.tsx`).
                        '&:hover': {
                            backgroundColor: getDropdownSurfaceActiveBackground(theme),
                        },
                        '&.Mui-disabled': { opacity: 0.4 },
                        // Same specificity as MUI's own `.MuiMenuItem-root .MuiListItemIcon-root`
                        // min-width rule, but injected later, so this wins.
                        '& .MuiListItemIcon-root': {
                            minWidth: 'auto',
                            color: theme.palette.custom.OV.sys.onSurfaceVariant,
                        },
                        // `ListItemText` wraps children in a `Typography`, and MUI's dense
                        // variant sizes the wrapper itself off `typography.body2` — both
                        // would otherwise override the row's per-size font.
                        '& .MuiListItemText-root, & .MuiListItemText-root .MuiTypography-root': {
                            fontSize: 'inherit',
                            fontWeight: 'inherit',
                            lineHeight: 1.45,
                        },
                        [`&.${v2MenuItemSizeClass.small}`]: {
                            gap: '0.4375rem',
                            padding: '0.375rem 0.5rem',
                            fontSize: '0.71875rem',
                            '& svg': { width: '0.75rem', height: '0.75rem', fontSize: '0.75rem' },
                        },
                        [`&.${v2MenuItemSizeClass.large}`]: {
                            gap: '0.625rem',
                            padding: '0.625rem 0.75rem',
                            fontSize: '0.875rem',
                            '& svg': { width: '1rem', height: '1rem', fontSize: '1rem' },
                        },
                        [`&.${v2MenuItemDangerClass}`]: {
                            color: theme.palette.custom.OV.sys.error,
                        },
                        [`&.${v2MenuItemDangerClass} .MuiListItemIcon-root`]: {
                            color: theme.palette.custom.OV.sys.error,
                        },
                        '& svg': { width: '0.8125rem', height: '0.8125rem', fontSize: '0.8125rem' },
                    }),
                },
            },
            MuiTextField: {
                defaultProps: { variant: 'outlined' },
            },
            MuiOutlinedInput: {
                styleOverrides: {
                    root: ({ theme }) => ({
                        borderRadius: 8,
                        backgroundColor: theme.palette.custom.OV.sys.surfaceContainer,
                        color: theme.palette.custom.OV.sys.onBackground,
                        fontSize: 13,
                        '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: theme.palette.custom.OV.sys.outlineVariant,
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: theme.palette.custom.OV.sys.outlineVariant,
                        },
                        // Mockup keeps a 1px border on focus and adds a soft outer ring
                        // instead of MUI's default 2px border swap. `sys.primary` is the
                        // mockup teal (per-mode), same source the checked switch uses.
                        '&.Mui-focused': {
                            boxShadow: `0 0 0 2px ${alpha(theme.palette.custom.OV.sys.primary, 0.28)}`,
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: theme.palette.custom.OV.sys.primary,
                            borderWidth: 1,
                        },
                        // MUI's default disabled state barely reads against the v2 fill, so
                        // recess the field: flatter container, dimmer 1px border, muted text.
                        '&.Mui-disabled': {
                            backgroundColor: theme.palette.custom.OV.sys.surfaceContainerLow,
                        },
                        '&.Mui-disabled .MuiOutlinedInput-notchedOutline': {
                            borderColor: theme.palette.custom.OV.sys.outlineVariant,
                            borderWidth: 1,
                            opacity: 0.5,
                        },
                        // Match the base disabled-text selector's specificity (root + input
                        // both carry Mui-disabled); injected later, so this wins.
                        '&.Mui-disabled .MuiInputBase-input.Mui-disabled': {
                            WebkitTextFillColor: theme.palette.custom.OV.sys.outline,
                        },
                    }),
                    input: ({ theme }) => ({
                        // 19px line + 8.5px vertical padding = the mockup's 36px field.
                        // MUI pins the input to 1.4375em, so pin height alongside line-height.
                        padding: '8.5px 12px',
                        height: '19px',
                        lineHeight: '19px',
                        '&::placeholder': {
                            color: theme.palette.custom.OV.sys.onSurfaceVariant,
                            opacity: 1,
                        },
                    }),
                },
            },
            MuiFormHelperText: {
                styleOverrides: {
                    root: ({ theme }) => ({
                        marginLeft: 0,
                        marginTop: 6,
                        fontSize: '11.5px',
                        lineHeight: 1.45,
                        color: theme.palette.custom.OV.sys.onSurfaceVariant,
                    }),
                },
            },
            // v2 tooltips read as a small surface card (border + shadow) instead of the
            // default dark chip. Deliberately no width — the tooltip hugs its content;
            // call sites cap width per-tooltip via `slotProps.tooltip.sx`.
            MuiTooltip: {
                styleOverrides: {
                    tooltip: ({ theme }) => ({
                        backgroundColor: theme.palette.custom.OV.sys.surfaceContainerHigh,
                        color: theme.palette.custom.OV.sys.onSurface,
                        border: `1px solid ${theme.palette.custom.OV.sys.outlineVariant}`,
                        borderRadius: 10,
                        padding: '10px 12px',
                        boxShadow: theme.shadows[3],
                        fontSize: 13,
                        lineHeight: 1.45,
                    }),
                    arrow: ({ theme }) => ({
                        color: theme.palette.custom.OV.sys.surfaceContainerHigh,
                        '&::before': {
                            border: `1px solid ${theme.palette.custom.OV.sys.outlineVariant}`,
                        },
                    }),
                },
            },
        },
    };
};
